import {
  Accordion,
  AccordionButton,
  AccordionItem,
  Box,
  Flex,
  Heading,
} from '@brand/components';
import { theme } from '@brand/theme/theme';
import { ArrowBackIcon } from '@chakra-ui/icons';
import { AccordionPanel } from '@chakra-ui/react';
import { useSinglePrismicDocument } from '@prismicio/react';
import { PrismicRichText } from 'components/atoms/PrismicRichText';
import BaseLayoutOrganism from 'components/organisms/layouts/Base';
import ComponentIsVisible from 'components/utils/IsVisible';
import { prismicImgixLoader } from 'helpers/prismicImgixLoader';
import useMediaQuery from 'hooks/useMediaQuery';
import { IPreFooterSlice, PrismicSliceType } from 'interfaces/Prismic';
import Image from 'next/image';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import Styles from './styles.module.css';

const HelpPage = () => {
  const { t, i18n } = useTranslation('common');
  const locale = useMemo(() => i18n.language, [i18n.language]);
  const dimension = useMediaQuery(768);

  const [selectedGroupOfQuestions, setSelectedGroupOfQuestions] = useState<
    string | null
  >();
  const [faqGroups, setFaqGroups] = useState([]);
  const [preFooterSlice, setPreFooterSlice] =
    useState<IPreFooterSlice | null>();

  const [prismicData] = useSinglePrismicDocument('help_page', {
    lang: locale === 'fr' ? 'fr-be' : 'nl-be',
  });
  useEffect(() => {
    const prismicSlices = prismicData?.data?.body?.map(
      (slice: PrismicSliceType) => {
        if (slice.slice_type === 'help_section_group_faq') {
          const questions = slice.items.map(question => question);
          return {
            sectionTitle: slice.primary.section_title,
            questions,
            sliceType: slice.slice_type,
          };
        } else if (slice.slice_type === 'prefooter') {
          return {
            preFooterTitle: slice.primary.prefooter_title,
            preFooterSubTitle: slice.primary.prefooter_subtitle,
            sliceType: slice.slice_type,
          };
        }
        return slice;
      },
    );

    if (!prismicSlices) return;
    setFaqGroups(
      prismicSlices.filter(
        (slice: PrismicSliceType) =>
          slice.sliceType === 'help_section_group_faq',
      ),
    );
    setPreFooterSlice(
      prismicSlices.find(
        (slice: PrismicSliceType) => slice.sliceType === 'prefooter',
      ),
    );
  }, [prismicData]);

  const handleOpenGroupOfQuestions = (section: string) => {
    setSelectedGroupOfQuestions(section as string);
  };
  const handleBackIcon = () => {
    setSelectedGroupOfQuestions(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderQuestionGroups = () => {
    if (!faqGroups) return;
    const formattedSlices = faqGroups.map(
      (questionsGroup: PrismicSliceType, index) => {
        const SectionTitle = questionsGroup.sectionTitle;
        return (
          <Flex
            key={uuid()}
            alignItems="center"
            justifyContent="center"
            p="1rem"
            borderRadius="0.5rem"
            transition="0.5s transform ease"
            margin={!dimension ? '4rem' : '1rem'}
            padding={
              !dimension ? '70px 100px 100px 100px' : '30px 10px 30px 10px'
            }
            width={!dimension ? '400px' : '300px'}
            height={!dimension ? '300px' : '150px'}
            className={
              index % 2 === 0
                ? Styles.questionGroupPair
                : Styles.questionGroupOdd
            }
            onClick={() => handleOpenGroupOfQuestions(SectionTitle as string)}
            _hover={{
              background: theme.colors.primary.base,
              cursor: 'pointer',
              transform: 'translateY(-10px)',
            }}
          >
            <Heading
              fontFamily={theme.fonts.heading}
              variant="h4"
              style={{ color: 'white', textAlign: 'center' }}
            >
              {SectionTitle}
            </Heading>
          </Flex>
        );
      },
    );
    return formattedSlices;
  };
  const renderQuestionGroup = () => {
    if (!faqGroups || faqGroups.length === 0) {
      return null;
    }

    const formattedQuestions = faqGroups.map(
      (questionsGroup: PrismicSliceType, index) => {
        const SectionTitle = questionsGroup.sectionTitle;

        if (SectionTitle === selectedGroupOfQuestions) {
          return (
            <Flex
              padding={
                !dimension ? '70px 100px 100px 100px' : '30px 10px 30px 10px'
              }
              width={!dimension ? '80vw' : '90vw'}
              borderRadius="0.5rem"
              key={`${SectionTitle}-${index}`}
              className={
                index % 2 === 0
                  ? Styles.questionGroupPair
                  : Styles.questionGroupOdd
              }
            >
              <Heading
                fontFamily={theme.fonts.heading}
                variant="h4"
                style={{ color: 'white' }}
              >
                {SectionTitle}
              </Heading>
              {questionsGroup.questions?.map((question: any) => (
                <Box width="100%" m="20px" key={question.question_title}>
                  <Accordion width="100%" allowToggle>
                    <AccordionItem>
                      <AccordionButton>
                        <Box flex="1" textAlign="left">
                          <Heading variant="h6">
                            {question.question_title}
                          </Heading>
                        </Box>
                      </AccordionButton>
                      <AccordionPanel px={['20px', '40px', '40px']}>
                        <PrismicRichText render={question.question_content} />
                        {question.question_image?.url && (
                          <Flex alignItems="center" justifyContent="center">
                            <Image
                              src={question.question_image.url}
                              alt={
                                question.question_image.alt ||
                                'missing alt description'
                              }
                              layout="fixed"
                              width="250px"
                              height="250px"
                              loader={prismicImgixLoader}
                            />
                          </Flex>
                        )}
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                </Box>
              ))}
            </Flex>
          );
        }

        return null;
      },
    );

    return formattedQuestions;
  };

  return (
    <BaseLayoutOrganism>
      <Heading textAlign="center">{t(`generic.help`)}</Heading>
      <Flex
        flex-wrap="wrap"
        padding="1rem 1.2rem"
        justifyContent="center"
        alignItems="center"
      >
        <ComponentIsVisible when={!!selectedGroupOfQuestions}>
          <Flex direction="column">
            <Flex
              w="100px"
              h="100px"
              p="1rem"
              m="auto"
              border="2.4px solid #405a6b"
              alignItems="center"
              justifyContent="center"
              borderRadius="100%"
              cursor="pointer"
              onClick={() => handleBackIcon()}
            >
              <ArrowBackIcon style={{ height: '50px', width: '100px' }} />
            </Flex>
            <Flex
              flexWrap="wrap"
              alignItems="center"
              m={!dimension ? '4rem' : '0rem'}
              justifyContent="center"
            >
              {faqGroups && renderQuestionGroup()}
            </Flex>
            <Flex
              w="100px"
              h="100px"
              p="1rem"
              m="auto"
              border="2.4px solid #405a6b"
              alignItems="center"
              justifyContent="center"
              borderRadius="100%"
              cursor="pointer"
              onClick={() => handleBackIcon()}
            >
              <ArrowBackIcon style={{ height: '50px', width: '100px' }} />
            </Flex>
          </Flex>
        </ComponentIsVisible>
        <ComponentIsVisible when={!selectedGroupOfQuestions}>
          <Flex flexWrap="wrap" alignItems="center" justifyContent="center">
            {faqGroups && renderQuestionGroups()}
          </Flex>
        </ComponentIsVisible>
      </Flex>
      <ComponentIsVisible when={!!preFooterSlice}>
        <Flex
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
          padding="30px"
          borderRadius="0.5rem"
          margin="0 auto"
          mb={4}
          w="80vw"
          backgroundColor={theme.colors.secondary}
        >
          <PrismicRichText
            color="white"
            render={
              (preFooterSlice as IPreFooterSlice) &&
              preFooterSlice?.preFooterTitle
            }
          />

          <PrismicRichText
            color="white"
            render={
              (preFooterSlice as IPreFooterSlice) &&
              preFooterSlice?.preFooterSubTitle
            }
          />
        </Flex>
      </ComponentIsVisible>
    </BaseLayoutOrganism>
  );
};

export default HelpPage;
